import { request as requester } from 'sonata-react-components';

import type {
  KpiOverview,
  PopularTimes,
  TrafficAnalysis
} from 'sonata-static-storage/lib/entities/footfallReport/footfallReport.types';
import type { BuyerInsightsApiResponse, MetricFilter, Poi, PoiQueryParams } from '@/types/footfall';

export type TrafficGroupBy = 'hour' | 'day';

export type FootfallPopularTimesResponse = {
  popularTimes: PopularTimes;
};

export type FootfallStatsResponse = {
  kpiOverview: KpiOverview;
};

export type FootfallTrafficStatsResponse = {
  trafficAnalysis: TrafficAnalysis;
};

export type AggregatedFootfallLineItemDataResponse = {
  lineItemData: {
    countries: string[];
    startDate: string;
    endDate: string;
    name: string;
    budget: number;
    lineItemCount: number;
    advertiserCount: number;
    campaignCount: number;
    pricingTypesCount: number;
    timezone?: string;
    lineItems: {
      id: string;
      name: string;
      countries: string[];
      campaignPlanName?: string;
      ioName?: string;
      poiCount?: number;
    }[];
  };
};

export type StoreDetailRow = {
  venueId: string;
  storeName: string;
  address?: string | null;
  brand?: string | null;
  upliftPercentage: number | null;
  uplift: number | null;
  baseline: number | null;
  costPerVisit: number | null;
  venueGeodataId: string;
  bestDay: string | null;
};

export type FootfallStoreDetailResponse = {
  items: StoreDetailRow[];
  totalItems: number;
};

export type MetricRangeFilter = { min?: number; max?: number };

export type MetricsUnitMode = 'values' | 'percentage';

export type MetricFilters = {
  baseline?: MetricRangeFilter;
  uplift?: MetricRangeFilter;
  cpsv?: MetricRangeFilter;
};

export type StoreDetailQueryParams = {
  country?: string;
  venueIds?: string[];
  nameFilter?: string;
  page?: number;
  itemsPerPage?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tagFilters?: Record<string, string[]>;
  metricFilters?: MetricFilters;
  metricsUnitMode?: MetricsUnitMode;
  brandFilter?: string[];
};

export async function getPoiList(
  entityType: string,
  entityId: string,
  userId: string,
  params?: PoiQueryParams
): Promise<Poi[]> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}multiflight/common/${entityType}/${entityId}/pois`, {
    data: { ...params, includePoiImageOverride: true, userId },
    method: 'POST'
  });
}

export type SetPoiImageOverrideResponse = {
  success: boolean;
  imageUrl: string;
};

export async function setPoiImageOverride(
  poiId: string,
  userId: string,
  rawFile: File
): Promise<SetPoiImageOverrideResponse> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}footfallReport/poi/${poiId}/image/${userId}`, {
    data: new Blob([rawFile]),
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    method: 'POST',
    params: {
      name: rawFile.name
    }
  });
}

export async function deletePoiImageOverride(poiId: string, userId: string): Promise<void> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}footfallReport/poi/${poiId}/image`, {
    data: { userId },
    method: 'DELETE'
  });
}

export type PoiWithUplift = Poi & {
  upliftPercentage: number | null;
  baseline: number | null;
  uplift: number | null;
  costPerVisit: number | null;
};

export async function getPoiListWithUplift(
  entityType: string,
  entityId: string,
  userId: string,
  params?: PoiQueryParams
): Promise<PoiWithUplift[]> {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/poisWithUplift`,
    {
      data: { ...params, userId },
      method: 'POST'
    }
  );
}

export async function getFootfallPopularTimes(
  entityType: string,
  entityId: string,
  venueId: string,
  userId: string
): Promise<FootfallPopularTimesResponse> {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/${venueId}/popularTimes`,
    {
      data: { userId },
      method: 'POST'
    }
  );
}

export async function getFootfallStats(
  entityType: string,
  entityId: string,
  userId: string,
  country?: string,
  venueIds?: string[]
): Promise<FootfallStatsResponse> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/stats`, {
    data: { country, userId, ...(venueIds?.length ? { venueIds } : {}) },
    method: 'POST'
  });
}

export type DateInterval = { start: string; end: string; timezone: string };

export type HeatmapPoiData = {
  venueId: string;
  name: string;
  address: string;
  peakTime: string | null;
  upliftAvg: number;
  dailyData: { date: string; uplift: number | null; baseline: number | null; cpsv: number | null }[];
  lineItemIds: string[];
};

export async function getHeatmapData(
  entityType: string,
  entityId: string,
  userId: string,
  dateInterval: DateInterval,
  metric: MetricFilter = 'percentage',
  venueIds?: string[],
  requestToken?: Record<string, unknown>
): Promise<HeatmapPoiData[]> {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/heatmap`,
    { data: { dateInterval, metric, userId, ...(venueIds?.length ? { venueIds } : {}) }, method: 'POST' },
    requestToken
  );
}

export async function getAggregatedFootfallLineItemData(
  entityType: string,
  entityId: string,
  userId: string,
  country?: string,
  opts?: { includePoiCount?: boolean }
): Promise<AggregatedFootfallLineItemDataResponse> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/lineItemData`, {
    data: { country, includePoiCount: opts?.includePoiCount, userId },
    method: 'POST'
  });
}

export async function getFootfallStoreDetail(
  entityType: string,
  entityId: string,
  userId: string,
  params?: StoreDetailQueryParams
): Promise<FootfallStoreDetailResponse> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/storeDetail`, {
    data: { ...params, userId },
    method: 'POST'
  });
}

export type StoreDetailFileFormat = 'csv' | 'excel';

export type StoreDetailFileQueryParams = Pick<StoreDetailQueryParams, 'country' | 'venueIds' | 'nameFilter'> & {
  fileFormat: StoreDetailFileFormat;
};

export async function getFootfallStoreDetailFile(
  entityType: string,
  entityId: string,
  userId: string,
  params: StoreDetailFileQueryParams
): Promise<void> {
  await requester.makeRequest(
    `${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/storeDetailFile`,
    {
      data: { ...params, userId },
      method: 'POST'
    }
  );
}

export async function getFootfallTrafficStats(
  entityType: string,
  entityId: string,
  userId: string,
  country?: string,
  venueIds?: string[],
  groupBy?: TrafficGroupBy,
  requestToken?: Record<string, any>
): Promise<FootfallTrafficStatsResponse> {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/trafficStats`,
    {
      data: {
        country,
        userId,
        ...(venueIds?.length ? { venueIds } : {}),
        ...(groupBy !== undefined ? { groupBy } : {})
      },
      method: 'POST'
    },
    requestToken
  );
}

export async function getBuyerInsights(
  entityType: string,
  entityId: string,
  geoDataId: string
): Promise<BuyerInsightsApiResponse> {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/${geoDataId}/buyerInsights`,
    { data: {}, method: 'POST' }
  );
}

export type FootfallReportNameResponse = {
  name: string | null;
};

export async function updateFootfallReportName(
  entityType: string,
  entityId: string,
  poiIds: string[] | undefined,
  name: string
): Promise<{ name: string }> {
  return requester.makeRequest(`${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/name`, {
    data: { name, ...(poiIds?.length ? { poiIds } : {}) },
    method: 'PATCH'
  });
}

export async function getFootfallReportName(
  entityType: string,
  entityId: string,
  poiIds?: string[]
): Promise<FootfallReportNameResponse> {
  const params = new URLSearchParams();
  if (poiIds?.length) {
    params.set('poiIds', poiIds.join(','));
  }

  return requester.makeRequest(`${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/name`, {
    method: 'GET',
    params
  });
}

export type LineItemComparisonRow = {
  id: string;
  name: string;
  campaignPlanName?: string;
  ioName?: string;
  poiCount: number;
  investment: number | null;
  uplift: number | null;
  upliftPct: number | null;
  costPerVisit: number | null;
  roi: number | null;
};

export type LineItemComparisonResponse = {
  items: LineItemComparisonRow[];
};

export async function getLineItemComparison(
  entityType: string,
  entityId: string,
  userId: string,
  country?: string
): Promise<LineItemComparisonResponse> {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/lineItemComparison`,
    {
      data: { country, userId },
      method: 'POST'
    }
  );
}

export type LineItemComparisonFileFormat = 'csv' | 'excel';

export async function getLineItemComparisonFile(
  entityType: string,
  entityId: string,
  userId: string,
  params: { fileFormat: LineItemComparisonFileFormat; country?: string; perPoi?: boolean }
): Promise<void> {
  await requester.makeRequest(
    `${taptap.config.backendUrlBase}footfallReport/${entityType}/${entityId}/lineItemComparisonFile`,
    {
      data: { ...params, userId },
      method: 'POST'
    }
  );
}
